/**
 * Pantalla de listas del usuario.
 * CRUD completo: Ver, Crear, Editar, Eliminar.
 *
 * Versión móvil:
 *  - isMobile reactivo con useMediaQuery (no window.innerWidth)
 *  - Cards compactas: avatar 40px, tipografía reducida, padding ajustado
 *  - Botones de acción más pequeños pero con área táctil mínima 44px
 *  - Header compacto en móvil (h5 en vez de h3, sin icono grande)
 *  - FAB "añadir" reposicionado para no chocar con la bottom bar
 *  - Fecha de creación visible si existe (campo fecha_creacion)
 *  - Botón invitar (solo móvil, solo listas con sala) → modal con email
 *  - Sección invitaciones pendientes (solo móvil nativo) → aceptar/rechazar
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Typography,
    IconButton,
    Avatar,
    useTheme,
    useMediaQuery,
    Tooltip,
    Fade,
    Zoom,
    Grid,
    Paper,
    Alert,
    Fab,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import AddIcon        from "@mui/icons-material/Add";
import EditIcon       from "@mui/icons-material/Edit";
import DeleteIcon     from "@mui/icons-material/Delete";
import ListAltIcon    from "@mui/icons-material/ListAlt";
import OpenInNewIcon  from "@mui/icons-material/OpenInNew";
import FolderIcon     from "@mui/icons-material/Folder";
import PersonAddIcon  from "@mui/icons-material/PersonAdd";
import CheckIcon      from "@mui/icons-material/Check";
import CloseIcon      from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";

import { motion, AnimatePresence } from "framer-motion";

import CrearListaModal        from "../components/CrearListaModal";
import EditarListaModal       from "../components/EditarListaModal";
import ConfirmarEliminarModal from "../components/ConfirmarEliminarModal";

import { obtenerListasDelUsuario, eliminarLista } from "../api/listas";
import { obtenerSalasDelUsuario }                 from "../api/salas";
import sqliteService                              from "../services/sqliteService";
import { Capacitor }                              from "@capacitor/core";

const MotionCard = motion(Card);

export default function Listas() {
    const navigate = useNavigate();
    const theme    = useTheme();

    /** isMobile reactivo — se actualiza al rotar el dispositivo */
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [cargando,      setCargando]      = useState(true);
    const [error,         setError]         = useState(null);
    const [listas,        setListas]        = useState([]);
    const [salas,         setSalas]         = useState([]);
    const [openCrear,     setOpenCrear]     = useState(false);
    const [openEditar,    setOpenEditar]    = useState(false);
    const [listaEditar,   setListaEditar]   = useState(null);
    const [openEliminar,  setOpenEliminar]  = useState(false);
    const [listaEliminar, setListaEliminar] = useState(null);

    // --- Estado para invitaciones pendientes (solo móvil nativo) ---
    const [invitacionesPendientes, setInvitacionesPendientes] = useState([]);
    const [gestionandoInv,         setGestionandoInv]         = useState(null); // id de la inv en proceso

    // --- Estado para el modal de invitar (solo móvil nativo) ---
    const [openInvitar,    setOpenInvitar]    = useState(false);
    const [listaInvitar,   setListaInvitar]   = useState(null);
    const [emailInvitar,   setEmailInvitar]   = useState("");
    const [invitando,      setInvitando]      = useState(false);
    const [mensajeInvitar, setMensajeInvitar] = useState(null); // { tipo: "ok"|"error", texto: string }

    /** Mapa idsala → nombre para el label de cada card */
    const salasMap = useMemo(() => {
        const m = new Map();
        salas.forEach((s) => {
            if (s?.idsala != null) m.set(Number(s.idsala), s.nombre);
        });
        return m;
    }, [salas]);

    /** Devuelve el nombre de la sala o "Lista personal" */
    const formatSalaLabel = (idsala) => {
        if (idsala == null) return "Lista personal";
        return salasMap.get(Number(idsala)) || `Sala #${idsala}`;
    };

    /** Carga listas y salas del usuario, con fallback a SQLite offline */
    async function cargarListas() {
        setCargando(true);
        setError(null);

        try {
            const [listasData, salasData] = await Promise.all([
                obtenerListasDelUsuario(),
                obtenerSalasDelUsuario(),
            ]);

            const listasArray = Array.isArray(listasData) ? listasData : [];
            setListas(listasArray);
            setSalas(Array.isArray(salasData) ? salasData : []);

            // Persistir offline en SQLite (solo plataforma nativa)
            for (const lista of listasArray) {
                sqliteService.saveListLocally(
                    lista.idlista.toString(),
                    lista.nombre,
                    lista.items || []
                ).catch((e) => console.error("Error guardando offline:", e));
            }
        } catch (err) {
            console.error(err);
            // Fallback: cargar de SQLite si no hay red
            const localLists = await sqliteService.getLocalLists();
            if (localLists.length > 0) {
                setListas(localLists.map((l) => ({
                    idlista: l.id,
                    nombre:  l.nombre,
                    items:   l.items,
                    offline: true,
                })));
                setError("Modo offline: mostrando datos locales.");
            } else {
                setError("No se pudieron cargar las listas y no hay datos locales.");
            }
        } finally {
            setCargando(false);
        }
    }

    useEffect(() => {
        cargarListas();
        cargarInvitaciones();
    }, []);

    /** Elimina la lista seleccionada y recarga */
    async function confirmarEliminar() {
        if (!listaEliminar?.idlista) return;
        try {
            await eliminarLista(listaEliminar.idlista);
            setOpenEliminar(false);
            setListaEliminar(null);
            await cargarListas();
        } catch (err) {
            console.error(err);
            setError("No se pudo eliminar la lista.");
        }
    }

    /** Carga invitaciones pendientes (solo en nativo) */
    async function cargarInvitaciones() {
        if (!Capacitor.isNativePlatform()) return;
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/salas/invitaciones/pendientes`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.ok) {
                const data = await res.json();
                setInvitacionesPendientes(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error("Error cargando invitaciones:", e);
        }
    }

    /** Acepta o rechaza una invitación */
    async function gestionarInvitacion(invId, accion) {
        setGestionandoInv(invId);
        try {
            const token = localStorage.getItem("token");
            await fetch(
                `${process.env.REACT_APP_API_URL}/salas/invitaciones/${invId}/${accion}`,
                { method: "POST", headers: { Authorization: `Bearer ${token}` } }
            );
            // Recarga invitaciones y listas tras aceptar
            await cargarInvitaciones();
            if (accion === "aceptar") await cargarListas();
        } catch (e) {
            console.error("Error gestionando invitación:", e);
        } finally {
            setGestionandoInv(null);
        }
    }

    /**
     * Envía invitación al usuario con el email introducido,
     * para que entre en la sala a la que pertenece la lista seleccionada.
     * Solo disponible en móvil nativo y en listas con sala.
     */
    async function enviarInvitacion() {
        if (!emailInvitar || !listaInvitar?.idsala) return;
        setInvitando(true);
        setMensajeInvitar(null);

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/salas/${listaInvitar.idsala}/invitar`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ email: emailInvitar }),
                }
            );

            if (!res.ok) {
                const data = await res.json();
                setMensajeInvitar({ tipo: "error", texto: data.detail || "Error al invitar" });
            } else {
                setMensajeInvitar({ tipo: "ok", texto: "¡Invitación enviada correctamente!" });
                setEmailInvitar("");
            }
        } catch {
            setMensajeInvitar({ tipo: "error", texto: "Error de conexión" });
        } finally {
            setInvitando(false);
        }
    }

    /** Cierra y limpia el modal de invitar */
    function cerrarModalInvitar() {
        setOpenInvitar(false);
        setListaInvitar(null);
        setEmailInvitar("");
        setMensajeInvitar(null);
    }

    return (
        <Box sx={{ p: { xs: 1.5, md: 4 }, maxWidth: 1000, mx: "auto" }}>

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <Fade in timeout={400}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: isMobile ? 2 : 4 }}
                >
                    <Box>
                        <Typography
                            variant={isMobile ? "h5" : "h3"}
                            sx={{
                                fontWeight: 900,
                                color:      "primary.main",
                                display:    "flex",
                                alignItems: "center",
                                gap:        1,
                            }}
                        >
                            {!isMobile && <ListAltIcon sx={{ fontSize: 36 }} />}
                            Mis Listas
                        </Typography>

                        {!isMobile && (
                            <Typography variant="body1" sx={{ opacity: 0.7, fontWeight: 500 }}>
                                Gestiona y organiza tus listas de la compra personales y compartidas.
                            </Typography>
                        )}
                    </Box>

                    {/* Botón nueva lista: icono en móvil, botón completo en escritorio */}
                    {isMobile ? (
                        <IconButton
                            onClick={() => setOpenCrear(true)}
                            aria-label="Nueva lista"
                            sx={{
                                bgcolor:      "primary.main",
                                color:        "white",
                                minWidth:     44,
                                minHeight:    44,
                                borderRadius: 2,
                                "&:hover":  { bgcolor: "primary.dark" },
                                "&:active": { transform: "scale(0.92)" },
                            }}
                        >
                            <AddIcon />
                        </IconButton>
                    ) : (
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCrear(true)}
                            sx={{
                                borderRadius:  3,
                                fontWeight:    700,
                                px:            3,
                                textTransform: "none",
                                boxShadow:     "0 4px 12px rgba(25, 118, 210, 0.3)",
                            }}
                        >
                            Nueva lista
                        </Button>
                    )}
                </Stack>
            </Fade>

            {/* ── INVITACIONES PENDIENTES (solo móvil nativo) ─────────── */}
            {Capacitor.isNativePlatform() && invitacionesPendientes.length > 0 && (
                <Fade in timeout={400}>
                    <Box sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <NotificationsIcon sx={{ fontSize: 18, color: "warning.main" }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "warning.main" }}>
                                Invitaciones pendientes ({invitacionesPendientes.length})
                            </Typography>
                        </Stack>
                        <Stack spacing={1}>
                            {invitacionesPendientes.map((inv) => (
                                <Paper
                                    key={inv.id}
                                    sx={{
                                        p: "10px 12px",
                                        borderRadius: 3,
                                        border: "1px solid",
                                        borderColor: "warning.light",
                                        bgcolor: alpha("#ff9800", 0.06),
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {inv.nombre_sala}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                            {new Date(inv.fecha).toLocaleDateString("es-ES")}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                                        {/* Botón aceptar */}
                                        <IconButton
                                            size="small"
                                            disabled={gestionandoInv === inv.id}
                                            onClick={() => gestionarInvitacion(inv.id, "aceptar")}
                                            sx={{
                                                width: 36, height: 36,
                                                bgcolor: alpha("#4caf50", 0.1),
                                                color: "success.main",
                                                borderRadius: 2,
                                                "&:hover": { bgcolor: "success.main", color: "white" },
                                                "&:active": { transform: "scale(0.92)" },
                                            }}
                                        >
                                            {gestionandoInv === inv.id
                                                ? <CircularProgress size={14} color="inherit" />
                                                : <CheckIcon sx={{ fontSize: 16 }} />
                                            }
                                        </IconButton>
                                        {/* Botón rechazar */}
                                        <IconButton
                                            size="small"
                                            disabled={gestionandoInv === inv.id}
                                            onClick={() => gestionarInvitacion(inv.id, "rechazar")}
                                            sx={{
                                                width: 36, height: 36,
                                                bgcolor: alpha("#f44336", 0.1),
                                                color: "error.main",
                                                borderRadius: 2,
                                                "&:hover": { bgcolor: "error.main", color: "white" },
                                                "&:active": { transform: "scale(0.92)" },
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                </Fade>
            )}

            {/* ── ESTADOS: cargando / error / vacío / lista ───────────── */}
            {cargando ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 2 }}>
                    <CircularProgress size={48} thickness={4} />
                    <Typography sx={{ opacity: 0.6, fontWeight: 600 }}>Cargando tus listas...</Typography>
                </Box>

            ) : error ? (
                <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>

            ) : listas.length === 0 ? (
                <Zoom in>
                    <Paper
                        sx={{
                            p:            isMobile ? 5 : 8,
                            textAlign:    "center",
                            borderRadius: 4,
                            bgcolor:      "rgba(0,0,0,0.02)",
                            border:       "2px dashed",
                            borderColor:  "divider",
                        }}
                    >
                        <FolderIcon sx={{ fontSize: 64, color: "divider", mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                            Aún no tienes ninguna lista
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.6, mb: 3 }}>
                            Crea tu primera lista para empezar a ahorrar tiempo y dinero.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCrear(true)}
                            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                        >
                            Crear mi primera lista
                        </Button>
                    </Paper>
                </Zoom>

            ) : (
                <Grid container spacing={isMobile ? 1.5 : 2}>
                    <AnimatePresence>
                        {listas.map((l, idx) => (
                            <Grid item xs={12} key={l.idlista}>
                                <ListaCard
                                    lista={l}
                                    idx={idx}
                                    isMobile={isMobile}
                                    theme={theme}
                                    formatSalaLabel={formatSalaLabel}
                                    onAbrir={() => navigate(`/listas/${l.idlista}`)}
                                    onEditar={() => { setListaEditar(l); setOpenEditar(true); }}
                                    onEliminar={() => { setListaEliminar(l); setOpenEliminar(true); }}
                                    // Invitar solo en nativo y si la lista tiene sala
                                    onInvitar={
                                        Capacitor.isNativePlatform() && l.idsala
                                            ? () => { setListaInvitar(l); setOpenInvitar(true); }
                                            : null
                                    }
                                />
                            </Grid>
                        ))}
                    </AnimatePresence>
                </Grid>
            )}

            {/* FAB "Nueva lista" — solo en escritorio */}
            {!isMobile && (
                <Fab
                    color="primary"
                    aria-label="Nueva lista"
                    onClick={() => setOpenCrear(true)}
                    sx={{
                        position:  "fixed",
                        bottom:    32,
                        right:     32,
                        zIndex:    999,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    }}
                >
                    <AddIcon />
                </Fab>
            )}

            {/* ── MODALES ─────────────────────────────────────────────── */}
            <CrearListaModal
                open={openCrear}
                onClose={() => { setOpenCrear(false); cargarListas(); }}
            />
            <EditarListaModal
                open={openEditar}
                lista={listaEditar}
                onClose={() => { setOpenEditar(false); setListaEditar(null); }}
                onUpdated={cargarListas}
            />
            <ConfirmarEliminarModal
                open={openEliminar}
                lista={listaEliminar}
                onClose={() => { setOpenEliminar(false); setListaEliminar(null); }}
                onConfirm={confirmarEliminar}
            />

            {/* Modal de invitar usuario a sala — solo móvil nativo */}
            <Dialog
                open={openInvitar}
                onClose={cerrarModalInvitar}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>
                    Invitar a {listaInvitar ? formatSalaLabel(listaInvitar.idsala) : ""}
                </DialogTitle>

                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Email del usuario"
                        type="email"
                        value={emailInvitar}
                        onChange={(e) => setEmailInvitar(e.target.value)}
                        // Enviar con Enter
                        onKeyDown={(e) => { if (e.key === "Enter") enviarInvitacion(); }}
                        sx={{ mt: 1 }}
                    />

                    {/* Mensaje de resultado (éxito o error) */}
                    {mensajeInvitar && (
                        <Alert
                            severity={mensajeInvitar.tipo === "ok" ? "success" : "error"}
                            sx={{ mt: 2, borderRadius: 2 }}
                        >
                            {mensajeInvitar.texto}
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={cerrarModalInvitar} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={enviarInvitacion}
                        disabled={invitando || !emailInvitar.trim()}
                        startIcon={<PersonAddIcon />}
                    >
                        {invitando ? "Enviando..." : "Invitar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

/* ─────────────────────────────────────────────────────────────────
 * ListaCard
 * Componente de card individual extraído para mantener Listas limpio.
 *
 * Props nuevas:
 *  - onInvitar: función | null — si es null, no muestra el botón invitar
 * ───────────────────────────────────────────────────────────────── */
function ListaCard({ lista: l, idx, isMobile, theme, formatSalaLabel, onAbrir, onEditar, onEliminar, onInvitar }) {
    const esSala = l.idsala != null;

    return (
        <MotionCard
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ delay: idx * 0.04, duration: 0.2 }}
            whileHover={!isMobile ? { x: 4, borderColor: theme.palette.primary.main } : undefined}
            sx={{
                borderRadius: 3,
                border:       "1px solid",
                borderColor:  "divider",
                boxShadow:    "0 2px 8px rgba(0,0,0,0.04)",
                transition:   "all 0.2s",
                "&:active": isMobile ? { opacity: 0.85 } : {},
            }}
        >
            <CardContent
                sx={{
                    display:        "flex",
                    justifyContent: "space-between",
                    alignItems:     "center",
                    p:              isMobile ? "12px 12px !important" : "20px !important",
                }}
            >
                {/* Zona izquierda: avatar + nombre + etiqueta sala */}
                <Stack direction="row" spacing={isMobile ? 1.5 : 2.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Avatar
                        sx={{
                            width:      isMobile ? 40 : 52,
                            height:     isMobile ? 40 : 52,
                            bgcolor:    esSala ? "secondary.main" : "primary.main",
                            boxShadow:  "0 2px 8px rgba(0,0,0,0.12)",
                            flexShrink: 0,
                        }}
                    >
                        <ListAltIcon sx={{ fontSize: isMobile ? 20 : 26 }} />
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant={isMobile ? "subtitle1" : "h6"}
                            sx={{
                                fontWeight:   800,
                                lineHeight:   1.2,
                                mb:           0.5,
                                overflow:     "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace:   "nowrap",
                            }}
                        >
                            {l.nombre}
                        </Typography>

                        {l.fecha_creacion && (
                            <Typography variant="caption" sx={{ opacity: 0.5, display: "block", lineHeight: 1, mb: 0.5 }}>
                                {new Date(l.fecha_creacion).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}
                            </Typography>
                        )}

                        <Chip
                            label={formatSalaLabel(l.idsala)}
                            size="small"
                            sx={{
                                height:     20,
                                fontSize:   "0.68rem",
                                fontWeight: 700,
                                bgcolor:    esSala
                                    ? alpha(theme.palette.secondary.main, 0.15)
                                    : alpha(theme.palette.primary.main, 0.12),
                                color:      esSala ? "secondary.main" : "primary.main",
                                border:     "none",
                            }}
                        />
                    </Box>
                </Stack>

                {/* Zona derecha: botones de acción */}
                <Stack direction="row" spacing={isMobile ? 0.25 : 1} sx={{ flexShrink: 0, ml: 1 }}>

                    {/* Abrir lista */}
                    <Tooltip title="Abrir lista" arrow>
                        <IconButton
                            onClick={onAbrir}
                            size="small"
                            aria-label="Abrir lista"
                            sx={{
                                width:        isMobile ? 36 : 44,
                                height:       isMobile ? 36 : 44,
                                bgcolor:      alpha(theme.palette.primary.main, 0.1),
                                color:        "primary.main",
                                borderRadius: 2,
                                "&:hover":  { bgcolor: "primary.main", color: "white" },
                                "&:active": { transform: "scale(0.92)" },
                            }}
                        >
                            <OpenInNewIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                        </IconButton>
                    </Tooltip>

                    {/* Invitar — solo si onInvitar viene definido (móvil nativo + lista con sala) */}
                    {onInvitar && (
                        <Tooltip title="Invitar a la sala" arrow>
                            <IconButton
                                onClick={onInvitar}
                                size="small"
                                aria-label="Invitar usuario"
                                sx={{
                                    width:        isMobile ? 36 : 44,
                                    height:       isMobile ? 36 : 44,
                                    bgcolor:      alpha(theme.palette.success.main, 0.1),
                                    color:        "success.main",
                                    borderRadius: 2,
                                    "&:hover":  { bgcolor: "success.main", color: "white" },
                                    "&:active": { transform: "scale(0.92)" },
                                }}
                            >
                                <PersonAddIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Editar */}
                    <Tooltip title="Editar nombre" arrow>
                        <IconButton
                            onClick={onEditar}
                            size="small"
                            aria-label="Editar lista"
                            sx={{
                                width:        isMobile ? 36 : 44,
                                height:       isMobile ? 36 : 44,
                                bgcolor:      alpha(theme.palette.info.main, 0.1),
                                color:        "info.main",
                                borderRadius: 2,
                                "&:hover":  { bgcolor: "info.main", color: "white" },
                                "&:active": { transform: "scale(0.92)" },
                            }}
                        >
                            <EditIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                        </IconButton>
                    </Tooltip>

                    {/* Eliminar */}
                    <Tooltip title="Eliminar lista" arrow>
                        <IconButton
                            onClick={onEliminar}
                            size="small"
                            aria-label="Eliminar lista"
                            sx={{
                                width:        isMobile ? 36 : 44,
                                height:       isMobile ? 36 : 44,
                                bgcolor:      alpha(theme.palette.error.main, 0.1),
                                color:        "error.main",
                                borderRadius: 2,
                                "&:hover":  { bgcolor: "error.main", color: "white" },
                                "&:active": { transform: "scale(0.92)" },
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </CardContent>
        </MotionCard>
    );
}